import { TextField, TextFieldProps } from '@rhg/libs/material';
import { forwardRef, ReactNode, Ref } from 'react';
import {
    Controller,
    ControllerProps,
    FieldValues,
    Path,
} from 'react-hook-form';

export type ValidatedTextFieldProps = TextFieldProps & {
    errorText?: string;
    label: string;
    name: string;
};

const ValidatedTextField = forwardRef(
    (
        {
            errorText,
            label,
            name,
            hiddenLabel,
            ...textFieldProps
        }: ValidatedTextFieldProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const fieldErrorID = `${name}-field-error`;
        return (
            <TextField
                name={name}
                error={!!errorText}
                label={hiddenLabel ? undefined : label}
                helperText={errorText}
                {...textFieldProps}
                FormHelperTextProps={{
                    ...textFieldProps.FormHelperTextProps,
                    // Must win over textFieldProps.FormHelperTextProps: this id
                    // is what InputProps['aria-describedby'] below points at, so
                    // it can never be silently overridden without breaking that
                    // link.
                    id: fieldErrorID,
                    // "status"/"polite" rather than "alert"/"assertive" — this
                    // fires after a user-initiated submit, not a system
                    // notification, and shouldn't interrupt in-progress
                    // speech (focus already moves to the first invalid field
                    // separately, so most SRs will reach this without needing
                    // an assertive interrupt).
                    role: errorText ? 'status' : undefined,
                    'aria-live': errorText ? 'polite' : undefined,
                }}
                InputProps={{
                    ...textFieldProps.InputProps,
                    'aria-describedby': fieldErrorID,
                }}
                inputProps={{
                    // Only inject an aria-label when there's no visible
                    // label for MUI to wire aria-labelledby/htmlFor to
                    // (i.e. hiddenLabel), and the caller hasn't already
                    // supplied its own aria-labelledby override. When a
                    // visible `label` is rendered, MUI already generates
                    // its own aria-labelledby pointing at it, so adding
                    // aria-label here as well produces a duplicated
                    // accessible name (e.g. "Gender, Gender" announced by
                    // screen readers).
                    ...(hiddenLabel &&
                    !textFieldProps.inputProps?.['aria-labelledby']
                        ? { 'aria-label': label }
                        : {}),
                    ...textFieldProps.inputProps,
                }}
                data-testid={`INPUT_${name}`}
                ref={ref}
            />
        );
    }
);

ValidatedTextField.displayName = 'ValidatedTextField';

export default ValidatedTextField;

type AdditionalControlledTextFieldProps = {
    textFieldProps?: Partial<ValidatedTextFieldProps>;
    label: string;
    required?: boolean;
    children?: ReactNode;
};

export function ControlledTextField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>
>({
    textFieldProps,
    label,
    required,
    children,
    ...controllerProps
}: Omit<ControllerProps<TFieldValues, TName>, 'render'> &
    AdditionalControlledTextFieldProps) {
    return (
        <Controller
            {...controllerProps}
            render={({ field, fieldState: { error } }) => (
                <ValidatedTextField
                    label={label}
                    {...textFieldProps}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(...event) => {
                        field.onChange(...event);
                        textFieldProps?.onChange?.(...event);
                    }}
                    inputRef={field.ref}
                    required={required}
                    error={!!error?.message}
                    errorText={error?.message}
                >
                    {children}
                </ValidatedTextField>
            )}
        />
    );
}
